import { useAtom } from "@xoid/react";
import { Headset } from "lucide-react";
import { Trans } from "react-i18next";
import { getBackendClient } from "@/lib/api-client";
import { getCashAgentUrl } from "@/lib/runtime-config";
import {
  createKioskTalkController,
  type KioskTalkController,
  type TalkListen,
} from "./kiosk-talk-controller";
import talkChimeUrl from "./talk-chime.wav";

const CHIME_VOLUME = 0.6;

export const listenViaBackend: TalkListen = async (sessionId, sdp) => {
  const { data, error } = await getBackendClient()
    ["kiosk-talk"].sessions({ sessionId })
    .listen.post({ sdp });
  if (error) {
    if (error.status === 404) return null;
    throw new Error(`kiosk-talk listen failed: ${error.status}`);
  }
  return data && "sdp" in data ? { sdp: data.sdp } : null;
};

const playChime = () => {
  const chime = new Audio(talkChimeUrl);
  chime.volume = CHIME_VOLUME;
  chime.play().catch((error) => console.warn("kiosk-talk: chime failed", error));
};

/** App-wide controller; `main.tsx` connects it once cash-agent's URL is known. */
export const kioskTalkController: KioskTalkController = createKioskTalkController({
  eventsUrl: () => `${getCashAgentUrl()}/kiosk-events`,
  listen: listenViaBackend,
  playChime,
});

/**
 * Thin strip hanging from the top edge for the whole talk session. Never takes
 * taps (`pointer-events-none`), so whatever is under it stays usable.
 */
export function KioskTalkBanner({
  controller = kioskTalkController,
}: {
  controller?: KioskTalkController;
}) {
  const session = useAtom(controller.session);
  if (!session) {
    return null;
  }
  return <KioskTalkBannerView controller={controller} />;
}

function KioskTalkBannerView({ controller }: { controller: KioskTalkController }) {
  const level = useAtom(controller.level);
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center"
      data-testid="kiosk-talk-banner"
      role="status"
    >
      <div className="flex items-center gap-3 rounded-b-2xl bg-bub-dark-blue py-2 pl-4 pr-6 text-white shadow-[0_10px_24px_-14px_rgba(0,59,85,0.7)] animate-in fade-in slide-in-from-top-4 duration-300">
        <span className="relative flex size-9 items-center justify-center">
          <span
            className="absolute inset-0 rounded-full bg-white/25 transition-transform duration-100 ease-out"
            style={{ transform: `scale(${0.7 + level * 0.6})` }}
          />
          <Headset size={22} strokeWidth={2.25} className="relative" />
        </span>
        <span className="text-[22px] font-semibold leading-none">
          <Trans>Bubbles ügyfélszolgálat beszél</Trans>
        </span>
      </div>
    </div>
  );
}
